import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import PasswordChangeModal from '../PasswordChangeModal';
import logo from 'assets/images/logo.png';
import supabase from '../../../supabase-client';

const AdminSidebar = ({ isCollapsed = false, onToggleCollapse, userName = 'Administrador' }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    {
      name: 'Principal',
      path: '/admin-dashboard-screen',
      icon: 'LayoutDashboard',
      badge: null
    },
    {
      name: 'Autores',
      path: '/admin-authors',
      icon: 'Feather',
      badge: null
    },
    {
      name: 'Categorías',
      path: '/admin-categories',
      icon: 'Layers',
      badge: null
    },
    {
      name: 'Catálogo',
      path: '/admin-catalog-management-screen',
      icon: 'BookOpen',
      badge: null
    },
    {
      name: 'Estadísticas',
      path: '/admin-reports',
      icon: 'BarChart3',
      badge: null
    }
  ];

  const handleLogout = () => {
    // Limpiar localStorage
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminEmail');
    
    navigate('/login-screen');
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block ${
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      } bg-primary border-r border-border transition-all duration-300`}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <Link to="/admin-dashboard-screen" className="flex items-center justify-center w-full">
              <img
                src={logo}
                alt="Logo Librería Lithay"
                className="w-60 h-10 object-contain"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1">
            {menuItems?.map((item) => {
              const isActive = location?.pathname === item?.path;
              
              return (
                <Link
                  key={item?.name}
                  to={item?.path}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-primary-foreground hover:bg-white/10 hover:text-accent'
                  }`}
                  title={isCollapsed ? item?.name : undefined}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={`flex-shrink-0 ${isCollapsed ? 'mx-auto' : 'mr-3'}`} 
                  />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1">{item?.name}</span>
                      {item?.badge && (
                        <span className="bg-accent text-accent-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {item?.badge}
                        </span>
                      )}
                    </>
                  )}
                  {isCollapsed && item?.badge && (
                    <span className="absolute left-8 top-1 bg-accent text-accent-foreground text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                      {item?.badge > 9 ? '9+' : item?.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="border-t border-white/10 p-2">
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`w-full text-primary-foreground hover:bg-white/10 hover:text-accent ${
                  isCollapsed ? 'px-2' : 'justify-start px-3'
                }`}
              >
                <Icon name="UserCircle" size={20} className={isCollapsed ? 'mx-auto' : 'mr-3'} />
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{userName}</span>
                    <Icon name="ChevronUp" size={16} />
                  </>
                )}
              </Button>

              {isUserMenuOpen && (
                <div className={`absolute ${isCollapsed ? 'left-16 bottom-0' : 'left-0 bottom-full'} mb-2 w-48 bg-popover border border-border rounded-md shadow-modal py-1 z-50`}>
                  <Link
                    to="/admin-help-screen"
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <Icon name="HelpCircle" size={16} className="inline mr-3" />
                    Ayuda
                  </Link>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setIsPasswordModalOpen(true);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                  >
                    <Icon name="Key" size={16} className="inline mr-3" />
                    Cambiar Contraseña
                  </button>
                  <hr className="my-1 border-border" />
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                  >
                    <Icon name="LogOut" size={16} className="inline mr-3" />
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <div className="lg:hidden">
        {!isCollapsed && (
          <div className="fixed inset-0 z-50 flex">
            <div 
              className="fixed inset-0 bg-black/50" 
              onClick={onToggleCollapse}
            />
            <div className="relative flex w-full max-w-xs flex-col bg-primary border-r border-border">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <Link to="/admin-dashboard-screen" className="flex items-center justify-center w-full">
                  <img
                    src={logo}
                    alt="Logo Librería Lithay"
                    className="w-10 h-10 object-contain"
                  />
                </Link>
              </div>

              <nav className="flex-1 px-2 py-4 space-y-1">
                {menuItems?.map((item) => {
                  const isActive = location?.pathname === item?.path;
                  
                  return (
                    <Link
                      key={item?.name}
                      to={item?.path}
                      onClick={onToggleCollapse}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                        isActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-primary-foreground hover:bg-white/10 hover:text-accent'
                      }`}
                    >
                      <Icon name={item?.icon} size={20} className="mr-3" />
                      <span className="flex-1">{item?.name}</span>
                      {item?.badge && (
                        <span className="bg-accent text-accent-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {item?.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>

              <div className="border-t border-white/10 p-2">
                <div className="space-y-1">
                  <Link
                    to="/admin-help-screen"
                    onClick={onToggleCollapse}
                    className="flex items-center px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-white/10 hover:text-accent rounded-md transition-smooth"
                  >
                    <Icon name="HelpCircle" size={20} className="mr-3" />
                    Ayuda
                  </Link>
                  <button
                    onClick={() => {
                      onToggleCollapse();
                      setIsPasswordModalOpen(true);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-white/10 hover:text-accent rounded-md transition-smooth"
                  >
                    <Icon name="Key" size={20} className="mr-3" />
                    Cambiar Contraseña
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-white/10 hover:text-accent rounded-md transition-smooth"
                  >
                    <Icon name="LogOut" size={20} className="mr-3" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </>
  );
};

export default AdminSidebar;
